import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";

type EntityMapping = {
  id: string;
  aliasTable: string;
  physicalSchema: string;
  physicalTable: string;
  columnMappings: Record<string, string>;
  enumMappings: Record<string, Record<string, string>>;
};

export function MappingsPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);

  const { data, isLoading } = useQuery<{ items: EntityMapping[] }>({
    queryKey: ["data-sources", id, "mappings"],
    queryFn: () => api.get(`/data-sources/${id}/mappings`),
  });

  const deleteMapping = useMutation({
    mutationFn: (mappingId: string) => api.del(`/data-sources/${id}/mappings/${mappingId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-sources", id, "mappings"] });
    },
  });

  if (isLoading) return <div className="text-sistolica-500 animate-pulse">Carregando mapeamentos...</div>;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-sistolica-500 mb-1">
            <Link to="/data-sources" className="hover:text-sistolica-800 transition">Bancos de Dados</Link> &bull; Mapeamentos
          </div>
          <h1 className="text-2xl font-semibold text-sistolica-800">Mapeamento Semântico</h1>
          <p className="text-sm text-sistolica-500 mt-1">Conecte as tabelas e colunas físicas do banco deste cliente às entidades do sistema.</p>
        </div>
        <button
          onClick={() => setIsAdding(!isAdding)}
          className="bg-sistolica-700 hover:bg-sistolica-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
        >
          {isAdding ? "Cancelar" : "+ Novo Mapeamento"}
        </button>
      </header>

      {isAdding && (
        <div className="bg-white p-6 rounded-xl border border-sistolica-200 shadow-sm">
          <h2 className="font-semibold text-sistolica-800 mb-4">Adicionar Mapeamento</h2>
          <MappingForm dataSourceId={id!} onSuccess={() => { setIsAdding(false); queryClient.invalidateQueries({ queryKey: ["data-sources", id, "mappings"] }); }} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data?.items.map((m) => (
          <div key={m.id} className="bg-white p-5 rounded-xl border border-sistolica-100 shadow-card flex flex-col">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-semibold text-sistolica-800 capitalize">{m.aliasTable}</h3>
                <div className="text-xs font-mono text-sistolica-500 mt-1 bg-sistolica-50 p-1.5 rounded">
                  {m.physicalSchema}.{m.physicalTable}
                </div>
              </div>
              <button
                onClick={() => { if (confirm("Remover este mapeamento?")) deleteMapping.mutate(m.id); }}
                className="text-xs text-red-500 hover:text-red-700 px-2 py-1"
              >
                Remover
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
              <div>
                <div className="text-[10px] uppercase font-bold text-sistolica-400 mb-1 tracking-wider">Colunas</div>
                <div className="text-xs bg-sistolica-50 rounded p-2 overflow-auto max-h-32 border border-sistolica-100">
                  {Object.entries(m.columnMappings).map(([k, v]) => (
                    <div key={k} className="flex justify-between mb-1 last:mb-0">
                      <span className="text-sistolica-600 font-medium">{k}</span>
                      <span className="text-sistolica-800">{v}</span>
                    </div>
                  ))}
                  {Object.keys(m.columnMappings).length === 0 && <span className="text-sistolica-400">Nenhum</span>}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-sistolica-400 mb-1 tracking-wider">Filtros (Enums)</div>
                <div className="text-xs bg-sistolica-50 rounded p-2 overflow-auto max-h-32 border border-sistolica-100">
                  {Object.entries(m.enumMappings).map(([filter, map]) => (
                    <div key={filter} className="mb-2 last:mb-0">
                      <div className="text-sistolica-600 font-medium border-b border-sistolica-200 mb-1">{filter}</div>
                      {Object.entries(map).map(([k, v]) => (
                        <div key={k} className="flex justify-between pl-2">
                          <span className="text-sistolica-500">{k}</span>
                          <span className="text-sistolica-800">{v}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                  {Object.keys(m.enumMappings).length === 0 && <span className="text-sistolica-400">Nenhum</span>}
                </div>
              </div>
            </div>
          </div>
        ))}
        {data?.items.length === 0 && !isAdding && (
          <div className="col-span-full py-12 text-center border-2 border-dashed border-sistolica-200 rounded-xl text-sistolica-500">
            Nenhum mapeamento configurado para este banco.
          </div>
        )}
      </div>
    </div>
  );
}

function MappingForm({ dataSourceId, onSuccess }: { dataSourceId: string, onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    aliasTable: "sales",
    physicalSchema: "public",
    physicalTable: "",
    columnMappingsStr: "{\n  \"total_amount\": \"valor_total\",\n  \"sale_date\": \"dt_venda\"\n}",
    enumMappingsStr: "{\n  \"status\": {\n    \"confirmed\": \"F\",\n    \"canceled\": \"C\"\n  }\n}"
  });
  const [error, setError] = useState<string | null>(null);
  
  const create = useMutation({
    mutationFn: async (data: any) => api.post(`/data-sources/${dataSourceId}/mappings`, data),
    onSuccess,
    onError: (err: any) => setError(err.message || "Erro ao salvar mapeamento.")
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        aliasTable: formData.aliasTable,
        physicalSchema: formData.physicalSchema,
        physicalTable: formData.physicalTable,
        columnMappings: JSON.parse(formData.columnMappingsStr),
        enumMappings: JSON.parse(formData.enumMappingsStr),
      };
      create.mutate(payload);
    } catch (err) {
      setError("JSON inválido nos campos de mapeamento.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm border border-red-200">{error}</div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-sistolica-600 mb-1">Entidade Semântica (Alias)</label>
          <select required value={formData.aliasTable} onChange={e => setFormData({ ...formData, aliasTable: e.target.value })} className="w-full border border-sistolica-200 rounded-md p-2 text-sm bg-white">
            <option value="sales">Vendas (sales)</option>
            <option value="sale_items">Itens de Venda (sale_items)</option>
            <option value="delinquencies">Inadimplência (delinquencies)</option>
            <option value="financial_entries">Financeiro (financial_entries)</option>
            <option value="targets">Metas (targets)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-sistolica-600 mb-1">Schema Físico</label>
          <input required type="text" value={formData.physicalSchema} onChange={e => setFormData({ ...formData, physicalSchema: e.target.value })} className="w-full border border-sistolica-200 rounded-md p-2 text-sm font-mono" placeholder="public" />
        </div>
        <div>
          <label className="block text-xs font-medium text-sistolica-600 mb-1">Tabela Física</label>
          <input required type="text" value={formData.physicalTable} onChange={e => setFormData({ ...formData, physicalTable: e.target.value })} className="w-full border border-sistolica-200 rounded-md p-2 text-sm font-mono" placeholder="tb_pedidos" />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-sistolica-600 mb-1">Column Mappings (JSON)</label>
          <textarea required rows={5} value={formData.columnMappingsStr} onChange={e => setFormData({ ...formData, columnMappingsStr: e.target.value })} className="w-full border border-sistolica-200 rounded-md p-2 text-xs font-mono bg-sistolica-50" placeholder='{"alias": "coluna_fisica"}' />
        </div>
        <div>
          <label className="block text-xs font-medium text-sistolica-600 mb-1">Enum Mappings (JSON)</label>
          <textarea required rows={5} value={formData.enumMappingsStr} onChange={e => setFormData({ ...formData, enumMappingsStr: e.target.value })} className="w-full border border-sistolica-200 rounded-md p-2 text-xs font-mono bg-sistolica-50" placeholder='{"filtro": {"alias": "valor_fisico"}}' />
        </div>
      </div>
      
      <div className="flex justify-end pt-2">
        <button type="submit" disabled={create.isPending} className="bg-gold-500 hover:bg-gold-600 text-sistolica-900 px-6 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50">
          {create.isPending ? "Salvando..." : "Salvar Mapeamento"}
        </button>
      </div>
    </form>
  );
}
